import { useState } from "react";
import { toast } from "sonner";
import { patchCandidate, deleteCandidate, draftOutreach } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";
import { CandidateNotes, FeatureToggle, SocialsPanel, TagPosts } from "@/components/scout/CandidateExtras";
import { CandidateActions, CandidateFacts, CandidateHeader, OutreachBlock } from "@/components/scout/CandidateParts";
import { RecordingPanel } from "@/components/scout/RecordingPanel";
import { DebatePrep } from "@/components/scout/DebatePrep";
import { ReplyTracker } from "@/components/scout/ReplyTracker";

export const CandidateCard = ({ c, onChange, onDelete }) => {
  const [busy, setBusy] = useState(false);

  const update = async (body) => {
    try {
      onChange(await patchCandidate(c.id, body));
    } catch (err) {
      toast.error(extractApiError(err, "Update failed."));
    }
  };

  const draft = async () => {
    setBusy(true);
    try {
      const { outreach_draft } = await draftOutreach(c.id);
      onChange({ ...c, outreach_draft });
      toast.success("Outreach draft ready");
    } catch (err) {
      toast.error(extractApiError(err, "Could not draft."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`scout-candidate-${c.id}`} className="border-t border-slate-200 pt-6 pb-2">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <CandidateHeader c={c} onChange={onChange} />
        <CandidateActions
          c={c}
          onStatus={(status) => update({ status })}
          onFeedback={(v) => update({ feedback: c.feedback === v ? "" : v })}
          onRemove={async () => {
            await deleteCandidate(c.id);
            onDelete(c.id);
          }}
        />
      </div>

      <p className="mt-4 text-sm text-slate-700 leading-relaxed max-w-3xl">{c.fit_reason}</p>

      <RecordingPanel key={`rec-${c.id}-${c.status}-${c.recording_at || ""}-${c.episode_url || ""}`} c={c} onChange={onChange} />
      <ReplyTracker c={c} onChange={onChange} />
      <SocialsPanel c={c} onChange={onChange} />
      <DebatePrep c={c} onChange={onChange} />
      <FeatureToggle c={c} onChange={onChange} />
      <TagPosts c={c} onChange={onChange} />
      <CandidateNotes key={`notes-${c.id}-${c.notes || ""}`} c={c} onChange={onChange} />
      <CandidateFacts c={c} />
      <OutreachBlock c={c} busy={busy} onDraft={draft} />
    </article>
  );
};

export default CandidateCard;
